import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import eventAPI from '../services/eventAPI';
import RoleBadge from '../components/RoleBadge';
import Navbar from '../components/Navbar';
import EventDetailsModal from '../components/EventDetailsModal';
import CalendarFeedSubscription from '../components/CalendarFeedSubscription';
import NotificationSettings from '../components/NotificationSettings';
import Footer from '../components/Footer';
import './Dashboard.css';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState([]);

  // Real event data
  const [events, setEvents] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showPastEvents, setShowPastEvents] = useState(false);

  useEffect(() => {
    fetchUser();
    generateCurrentWeek();
  }, []);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // Auto-refresh events every minute
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      loadData();
    }, 60000); // 60 seconds

    return () => clearInterval(interval);
  }, [user]);

  const fetchUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      // Load events and invitations in parallel
      const [eventsData, invitationsData] = await Promise.all([
        eventAPI.getEvents(),
        eventAPI.getPendingInvitations()
      ]);

      setEvents(eventsData.events || []);
      setPendingInvitations(invitationsData.invitations || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const generateCurrentWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    const week = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      week.push(date);
    }
    setCurrentWeek(week);
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  const getDayName = (date) => {
    return date.toLocaleDateString('de-DE', { weekday: 'short' });
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      if (!event.finalized_datetime) return false;
      const eventDate = new Date(event.finalized_datetime);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getWeekEvents = () => {
    const weekStart = currentWeek[0];
    const weekEnd = new Date(currentWeek[6]);
    weekEnd.setHours(23, 59, 59);

    return events.filter(event => {
      if (!event.finalized_datetime) return false;
      const eventDate = new Date(event.finalized_datetime);
      return eventDate >= weekStart && eventDate <= weekEnd;
    });
  };

  const getPlanningEvents = () => {
    return pendingInvitations.filter(inv => inv.event_type === 'planning' && inv.status === 'planning');
  };

  const getMyEvents = () => {
    const myEvents = events.filter(event => event.creator_id === user?.id);

    if (!showPastEvents) {
      // Filter out past events
      return myEvents.filter(event => {
        const eventDate = event.finalized_datetime ? new Date(event.finalized_datetime) : null;
        if (!eventDate) return true; // Keep events without dates (planning events)
        return eventDate >= new Date(); // Only show future/today events
      });
    }

    return myEvents;
  };

  const handleRespondToInvitation = async (eventId, status) => {
    try {
      await eventAPI.respondToInvitation(eventId, status);
      loadData();
    } catch (error) {
      console.error('Failed to respond to invitation:', error);
      alert('Antwort konnte nicht gespeichert werden');
    }
  };

  const handleOpenEventDetails = (eventId) => {
    // Find the event from events array
    const event = events.find(e => e.id === eventId);
    if (!event) {
      setSelectedEventId(eventId);
      return;
    }

    // Check if event should open recap page
    const isFinalized = event.event_type === 'direct' ||
                        event.status === 'scheduled' ||
                        event.status === 'confirmed';

    const eventDate = event.finalized_datetime ? new Date(event.finalized_datetime) : null;
    const isPastOrToday = eventDate && eventDate <= new Date();

    if (isFinalized && isPastOrToday) {
      // Navigate to recap page
      navigate(`/event/${eventId}/recap`);
    } else {
      // Open modal
      setSelectedEventId(eventId);
    }
  };

  const handleCloseEventDetails = () => {
    setSelectedEventId(null);
    loadData(); // Reload data when modal closes
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="dashboard-container">
          <div className="retro-loading">
            <div className="loading-spinner"></div>
            <p>LOADING...</p>
          </div>
        </div>
      </>
    );
  }

  const planningEvents = getPlanningEvents();

  return (
    <>
      <Navbar />
      <div className="dashboard-container">
        <main className="dashboard-content">
          {/* Welcome Header */}
          <div className="dashboard-header">
            <div className="welcome-section">
              <h1 className="dashboard-title">
                <span className="title-bracket">{'['}</span>
                WILLKOMMEN
                <span className="title-bracket">{']'}</span>
              </h1>
              <p className="dashboard-subtitle">{user?.username || 'User'}</p>
              {user?.groups && user.groups.length > 0 && (
                <div className="user-roles">
                  {user.groups.map(group => (
                    <RoleBadge key={group} role={group} size="small" />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Current Week Overview */}
          <section className="dashboard-section week-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">[#]</span>
                DIESE WOCHE
              </h2>
              <button className="retro-button-small" onClick={() => navigate('/calendar')}>
                ZUM KALENDER →
              </button>
            </div>
            <div className="week-grid">
              {currentWeek.map((date, index) => {
                const dayEvents = getEventsForDate(date);
                return (
                  <div
                    key={index}
                    className={`week-day ${isToday(date) ? 'today' : ''}`}
                  >
                    <div className="week-day-header">
                      <span className="week-day-name">{getDayName(date)}</span>
                      <span className="week-day-date">{formatDate(date)}</span>
                    </div>
                    <div className="week-day-events">
                      {dayEvents.length > 0 ? (
                        dayEvents.map(event => {
                          const eventDate = new Date(event.finalized_datetime);
                          const timeStr = eventDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                          return (
                            <div
                              key={event.id}
                              className={`week-event event-${event.color}`}
                              onClick={() => handleOpenEventDetails(event.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="event-time">{timeStr}</span>
                              <span className="event-title">{event.title}</span>
                            </div>
                          );
                        })
                      ) : (
                        <span className="no-events">-</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* My Events Section */}
          <section className="dashboard-section my-events-section">
            <div className="section-header">
              <div className="section-title-wrapper">
                <h2 className="section-title">
                  <span className="section-icon">[*]</span>
                  MEINE EVENTS
                </h2>
                <span className="badge-count">{getMyEvents().length}</span>
              </div>
              <button
                className="retro-button-small"
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                {showPastEvents ? 'VERGANGENE AUSBLENDEN' : 'VERGANGENE ANZEIGEN'}
              </button>
            </div>
            <div className="my-events-list">
              {getMyEvents().length > 0 ? (
                getMyEvents().map(event => {
                  const isDirect = event.event_type === 'direct';
                  const isPlanning = event.event_type === 'planning';
                  const isFinalized = event.status === 'scheduled' || event.status === 'confirmed';
                  const isProposed = event.status === 'planning';

                  const eventDate = event.finalized_datetime
                    ? new Date(event.finalized_datetime).toLocaleString('de-DE')
                    : event.voting_deadline
                    ? `Voting bis ${new Date(event.voting_deadline).toLocaleDateString('de-DE')}`
                    : 'TBD';

                  let statusIcon = '';
                  let statusText = '';
                  let cardClass = '';

                  if (isDirect) {
                    statusIcon = '[#] ';
                    statusText = 'FESTER TERMIN';
                    cardClass = 'direct';
                  } else if (isPlanning && isFinalized) {
                    statusIcon = '[✓] ';
                    statusText = 'TERMIN BESTÄTIGT';
                    cardClass = 'finalized';
                  } else if (isProposed) {
                    statusIcon = '[?] ';
                    statusText = 'WARTEN AUF ABSTIMMUNG';
                    cardClass = 'proposed';
                  }

                  const colorMap = {
                    purple: '#9d5cff',
                    pink: '#ec4899',
                    blue: '#3b82f6',
                    green: '#10b981',
                    orange: '#f59e0b',
                    red: '#ef4444'
                  };
                  const eventColor = colorMap[event.color] || event.color;

                  return (
                    <div
                      key={event.id}
                      className={`my-event-card ${cardClass}`}
                      onClick={() => handleOpenEventDetails(event.id)}
                      style={{ cursor: 'pointer', borderLeft: `6px solid ${eventColor}` }}
                    >
                      <div className="my-event-header">
                        <div className="my-event-title-wrapper">
                          <span
                            className="event-color-dot"
                            style={{ backgroundColor: eventColor }}
                          ></span>
                          <h3 className="my-event-title">
                            <span className="event-status-icon">{statusIcon}</span>
                            {event.title}
                          </h3>
                        </div>
                        <span className={`event-type-badge ${isPlanning ? 'planning' : 'direct'}`}>
                          {isPlanning ? '[V] VOTING' : '[#] DIREKT'}
                        </span>
                      </div>
                      <div className="my-event-info">
                        <span className="my-event-date">{eventDate}</span>
                        <span className="my-event-invites">
                          {(isDirect || isFinalized)
                            ? `${event.accepted_count || 0} Dabei`
                            : `${event.invitation_count || 0} Eingeladen`
                          }
                        </span>
                      </div>
                      <div className={`my-event-status ${cardClass}`}>
                        {statusText}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <p>Du hast noch keine Events erstellt</p>
                </div>
              )}
            </div>
          </section>

          {/* Open Invites Section */}
          <section className="dashboard-section invites-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">[M]</span>
                OFFENE EINLADUNGEN
              </h2>
              <span className="badge-count">
                {pendingInvitations.filter(inv => inv.status === 'scheduled').length}
              </span>
            </div>
            <div className="invites-list">
              {pendingInvitations.filter(inv => inv.status === 'scheduled').length > 0 ? (
                pendingInvitations
                  .filter(inv => inv.status === 'scheduled')
                  .map(invitation => {
                    const eventDate = invitation.finalized_datetime
                      ? new Date(invitation.finalized_datetime).toLocaleDateString('de-DE')
                      : 'TBD';

                    return (
                      <div key={invitation.id} className="invite-card">
                        <div className="invite-header">
                          <h3 className="invite-title">{invitation.title}</h3>
                          <span className="invite-date">{eventDate}</span>
                        </div>
                        <p className="invite-from">Von: {invitation.creator_username}</p>
                        <div className="invite-actions">
                          <button
                            className="retro-button retro-button-success"
                            onClick={() => handleRespondToInvitation(invitation.id, 'accepted')}
                          >
                            ✓ ANNEHMEN
                          </button>
                          <button
                            className="retro-button retro-button-danger"
                            onClick={() => handleRespondToInvitation(invitation.id, 'declined')}
                          >
                            ✕ ABLEHNEN
                          </button>
                        </div>
                      </div>
                    );
                  })
              ) : (
                <div className="empty-state">
                  <p>Keine offenen Einladungen</p>
                </div>
              )}
            </div>
          </section>

          {/* Open Votes Section (Planning Events) */}
          <section className="dashboard-section votes-section">
            <div className="section-header">
              <h2 className="section-title">
                <span className="section-icon">[V]</span>
                OFFENE ABSTIMMUNGEN
              </h2>
              <span className="badge-count">{planningEvents.length}</span>
            </div>
            <div className="votes-list">
              {planningEvents.length > 0 ? (
                planningEvents.map(invitation => {
                  const datetimeOptions = invitation.options?.filter(
                    opt => opt.option_type === 'datetime' && opt.approved
                  ) || [];

                  return (
                    <div key={invitation.id} className="vote-card">
                      <div className="vote-header">
                        <h3 className="vote-title">{invitation.title}</h3>
                        <span className="vote-creator">von {invitation.creator_username}</span>
                      </div>

                      {invitation.description && (
                        <p className="vote-description">{invitation.description}</p>
                      )}

                      {invitation.voting_deadline && (
                        <p className="vote-deadline">
                          [!] Abstimmung endet: {new Date(invitation.voting_deadline).toLocaleDateString('de-DE')}
                        </p>
                      )}

                      <div className="vote-options-preview">
                        <p className="vote-options-label">Optionen:</p>
                        {datetimeOptions.slice(0, 3).map(option => {
                          const totalVotes = option.votes?.total || 0;
                          return (
                            <div key={option.id} className="vote-option-preview">
                              <span className="vote-option-preview-value">
                                {new Date(option.option_value).toLocaleString('de-DE')}
                              </span>
                              <span className="vote-option-preview-count">
                                {totalVotes} {totalVotes === 1 ? 'Stimme' : 'Stimmen'}
                              </span>
                            </div>
                          );
                        })}
                        {datetimeOptions.length > 3 && (
                          <p className="vote-more">+ {datetimeOptions.length - 3} weitere</p>
                        )}
                      </div>

                      <button
                        className="vote-details-btn"
                        onClick={() => handleOpenEventDetails(invitation.id)}
                      >
                        ABSTIMMEN →
                      </button>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">
                  <p>Keine offenen Abstimmungen</p>
                </div>
              )}
            </div>
          </section>

          {/* Calendar Feed Subscription */}
          <CalendarFeedSubscription />

          {/* Notification Settings */}
          <section className="dashboard-section notification-section">
            <NotificationSettings />
          </section>
        </main>

        <Footer />
      </div>

      {/* Event Details Modal */}
      {selectedEventId && (
        <EventDetailsModal
          eventId={selectedEventId}
          onClose={handleCloseEventDetails}
          onEventUpdated={loadData}
          currentUserId={user?.id}
        />
      )}
    </>
  );
}

export default Dashboard;
